import React, { useState } from "react";

import Editor from "@monaco-editor/react";
import { T } from "services/lang/LanguageService";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { DashboardReportMarginBoxes, MarginBox, MetaOptions, PageLayout, TimingOptions } from "api/model/dashboard/DashboardReportOptions";
import { DashboardTabModel } from "api/model/dashboard/DashboardTab";
import { Button } from "primereact/button";
import { TabPanel, TabView } from "primereact/tabview";
import GeneralSubForm from "./DashboardReportEditorComponents/DashboardReportEditorForms/GeneralSubFrom";
import MetaOptionsSubForm from "./DashboardReportEditorComponents/DashboardReportEditorForms/MetaOptionsSubForm";
import PageLayoutSubForm from "./DashboardReportEditorComponents/DashboardReportEditorForms/PageLayoutSubForm";
import DashboardTabsSubForm from "./DashboardReportTabEditor/DashboardReportTabEditor";
import TimingOptionsSubForm from "./DashboardReportEditorComponents/DashboardReportEditorForms/TimingOptionsSubForm";

const DashboardReportEditor = (props: {
  dashboard: Dashboard;
  cancel: () => void;
  onChange: (updatedConfig: Dashboard) => void;
}) => {
  const [def, setDef] = useState<string | undefined>(JSON.stringify(props.dashboard, null, 2));
  const [config, setConfig] = useState<Dashboard>(props.dashboard);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [activeIndex, setActiveIndex] = useState(0);
  const [newGeneralSub, setNewGeneralSub] = useState<object>({});

  const handleSave = () => {
    const updatedConfig = {
      ...config,
      ...newGeneralSub,
    };
    setConfig(updatedConfig);
    props.onChange(updatedConfig);
  };

  const updateConfig = (dashboard: Dashboard) => {
    setConfig(dashboard);
    setDef(JSON.stringify(dashboard, null, 2));
  };

  const onEditorChange = (newValue: string | undefined) => {
    setDef(newValue);
    try {
      const val = JSON.parse(newValue!!);
      setConfig(val);
      setErrorMessage(undefined);
    } catch (err) {
      setErrorMessage(`${T("error.failed-to-parse-json")}: ${(err as Error).message}`);
    }
  };

  const onChangePageLayout = (pageLayout: PageLayout) => {
    const cfgNew = {
      ...config,
      reportOptions: { ...config.reportOptions, pageLayout },
    };
    updateConfig(cfgNew);
  };

  const onChangeMarginBoxes = (key: keyof DashboardReportMarginBoxes, o: MarginBox) => {
    const cfgNew = {
      ...config,
      reportOptions: { ...config.reportOptions, marginBoxes: { ...config.reportOptions.marginBoxes, [key]: o } },
    };
    updateConfig(cfgNew);
  };

  const onChangeTimingOptions = (timingOptions: TimingOptions) => {
    const cfgNew = {
      ...config,
      reportOptions: { ...config.reportOptions, timingOptions },
    };
    updateConfig(cfgNew);
  };

  const onChangeMetaOptions = (metaOptions?: MetaOptions) => {
    const cfgNew = {
      ...config,
      reportOptions: { ...config.reportOptions, meta: metaOptions },
    } as Dashboard;
    updateConfig(cfgNew);
  };

  const onChangeDashboardTabs = (tabs: Array<DashboardTabModel>) => {
    const cfgNew: Dashboard = {
      ...config,
      dashboardTabsJs: tabs,
    };
    updateConfig(cfgNew);
  };

  return (
    <div className="flex flex-column">
      <TabView activeIndex={activeIndex} onTabChange={e => setActiveIndex(e.index)}>
        <TabPanel header={T("word.general")}>
          <div>
            <GeneralSubForm dashboard={config} setNewGeneralSub={setNewGeneralSub} />
          </div>
          <div>
            <TimingOptionsSubForm
              timingOptions={config?.reportOptions.timingOptions}
              onChange={onChangeTimingOptions}
            />
          </div>
          <div>
            <PageLayoutSubForm
              marginBoxes={config?.reportOptions.marginBoxes}
              pageLayout={config?.reportOptions.pageLayout!!}
              onChange={onChangePageLayout}
              onChangeMarginBoxes={onChangeMarginBoxes}
            />
          </div>
          <div>
            <MetaOptionsSubForm
              metaOptions={config?.reportOptions.meta}
              onChange={onChangeMetaOptions}
            />
          </div>
        </TabPanel>
        <TabPanel header={T("word.tabs")}>
          <DashboardTabsSubForm
            dashboardTabs={config?.dashboardTabsJs}
            onChange={onChangeDashboardTabs}
          />
        </TabPanel>
        <TabPanel header="JSON">
          <Editor
            height="500px"
            width="100%"
            defaultLanguage="json"
            value={def}
            onChange={onEditorChange}
            theme="vs-dark"
          />
        </TabPanel>
      </TabView>

      {errorMessage && <div className="p-error">{errorMessage}</div>}

      <div className="flex-none flex justify-content-center gap-2 mt-2" style={{ width: "100%" }}>
        <Button
          label={T("action.save")}
          className="p-button-success w-full"
          icon="pi pi-check"
          onClick={handleSave}
          disabled={errorMessage !== undefined}
        />
        <Button
          label={T("word.cancel")}
          className="p-button-danger w-full"
          icon="pi pi-times"
          onClick={props.cancel}
        />
      </div>
    </div>
  );
};

export default DashboardReportEditor;
